import React, { useEffect, useState, useRef } from "react";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import { Box, makeStyles, Typography, Dialog, IconButton } from "@material-ui/core";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import { useTranslate } from "react-polyglot";
import coins from "../../assets/ic_coins.png";
import oneCoin from "../../assets/ic_one_coin.png";
import target from "../../assets/ic_target.svg";
import shoppingCart from "../../assets/ic_shopping-cart.svg";
import targetCompleted from "../../assets/ic_target_completed.svg";
import { useProductCatalog } from "../../Providers/Data/ProductCatalogProvider.jsx";
import { useMissions } from "../../Providers/Data/MissionsProvider.jsx";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import useDateUtils from "../../hooks/useDateUtils.js";
import BaseAccordion from "../../SharedComponents/BaseAccordion.jsx";
import { useDialogs } from "../../Providers/Common/DialogsProvider.jsx";
import MissionStartButton from "../../SharedComponents/MissionStartButton.jsx";
import { loadObjectFromStorage, saveObjectToStorage } from "../../Utils/StorageUtils.js";
import TaskDetails from "./TaskDetails.jsx";

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: "auto",
    padding: "35px 20px",
    [theme.breakpoints.up("lg")]: {
      paddingLeft: 0,
      paddingRight: 0,
      maxWidth: 700,
      width: 700,
      alignSelf: "center",
    },
  },
  rewardWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 35,
    position: "relative",
  },
  title: {
    fontSize: 24,
    fontWeight: 500,
    color: "#1B1C1C",
  },
  rewardImg: {
    width: 40,
    height: 40,
  },
  accordion: {
    marginBottom: theme.spacing(1),
    backgroundColor: "#fff",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(2),
    "& .MuiCollapse-wrapper": {
      padding: "0 16px 16px 16px",
    },
  },
  head: {
    "& .MuiAccordionSummary-content": {
      margin: 0,
      alignItems: "center",
    },
    "& .MuiIconButton-root": {
      color: "#909293",
    },
  },
  headIcon: {
    width: 50,
    height: 60,
    marginRight: 35,
  },
  headTitle: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 5,
  },
  headText: {
    fontSize: 12,
  },
  accordionItem: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 25px",
    backgroundColor: "#fff",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(1.5),
    marginTop: 8,
    "& .MuiSvgIcon-root": {
      fill: "#909293",
    },
    cursor: "pointer",
  },
  learnedAccordionItem: {
    backgroundColor: "#c5ffc9",
  },
  accordionContent: {
    padding: 0,
    boxShadow: "none",
    flexDirection: "column",
  },
  accordionReward: {
    width: 25,
    height: 25,
  },
  contentTitle: {
    color: "#3d4853",
    fontSize: 14,
    fontWeight: 600,
  },
  learnedContentTitle: {
    color: "#003915",
  },
  contentText: {
    color: "#54606B",
    fontSize: 12,
  },
  dividerCoins: {
    color: "rgba(0, 0, 0, .08)",
    fontSize: 24,
    margin: "0px 16px",
  },
  coinBalance: {
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.5),
  },
  shoppingIconButton: {
    position: "absolute",
    top: 0,
    right: 0,
  },
}));

/**
 * @returns {*}
 * @constructor
 */
const Missions = () => {
  const classes = useStyles();
  const t = useTranslate();
  const history = useHistory();
  const { missions, missionRewards, setMissionRewards, startMission } = useMissions();
  const { coinBalance } = useBaseData();
  const { format } = useDateUtils();
  const { warn, success, alert } = useDialogs();
  const [taskDetails, setTaskDetails] = useState(null);
  const [expandedArr, setExpandedArr] = useState([]);
  const { coinOffers } = useProductCatalog();
  const lastExpandedIdRef = useRef(loadObjectFromStorage("expanded_mission_id"));

  /** */
  const accordionExpandHandler = (id, event, expanded) => {
    if (expanded) {
      saveObjectToStorage("expanded_mission_id", id);
      expandedArr.push(id);
      setExpandedArr(expandedArr);
    } else {
      const newExpandedArr = expandedArr.filter((item) => item !== id);
      if (newExpandedArr.length > 0 && id === expandedArr[expandedArr.length - 1]) {
        saveObjectToStorage("expanded_mission_id", newExpandedArr[newExpandedArr.length - 1]);
      }
      setExpandedArr(newExpandedArr);
      if (newExpandedArr.length === 0) {
        localStorage.removeItem("expanded_mission_id");
        return;
      }
    }
  };

  /** */
  const onCloseRewards = () => {
    const newRewards = _.cloneDeep(missionRewards);
    newRewards.shift();
    setMissionRewards(newRewards);
  };

  /** */
  const onStartMission = async (id) => {
    if (!id) {
      warn(t("totalError"));
      return;
    }
    await startMission(id);
    success(t("missions.started"));
  };

  /** */
  const onClose = () => {
    setTaskDetails(null);
  };

  // При наличии наград выводит модальное окно с поздравлением пользователю
  useEffect(() => {
    if (missionRewards.length > 0) {
      alert({
        confirmText: "OK",
        onConfirm: onCloseRewards,
        title: t("congratulation.mission", { name: missionRewards[0].content }),
        message: missionRewards[0].congratulationText,
        maxWidth: "md",
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionRewards]);

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.rewardWrapper}>
        <Typography className={classes.title}>{t("missions.title")}</Typography>
        <Typography className={classes.dividerCoins}>|</Typography>
        {/* Баланс монет может быть дробным (распределение наград за миссии по предметам), отображаем округлённо */}
        <Typography className={classes.coinBalance}>{Math.round(coinBalance)}</Typography>
        <img className={classes.rewardImg} src={coins} alt="coins" />
        {coinOffers.length > 0 && (
          <IconButton
            className={classes.shoppingIconButton}
            color="inherit"
            onClick={() => {
              history.push("/missions/product_catalog");
            }}
          >
            <img className={classes.shoppingIcon} src={shoppingCart} alt="shoppingCart" />
          </IconButton>
        )}
      </Box>
      {missions.map((mission) => (
        <BaseAccordion
          key={mission.id}
          className={classes.accordion}
          defaultExpanded={mission.id === lastExpandedIdRef.current}
          onChange={(event, expanded) => accordionExpandHandler(mission.id, event, expanded)}
          summaryClassName={classes.head}
          summary={
            <>
              <img className={classes.headIcon} src={mission.learned ? targetCompleted : target} alt="target" />
              <div>
                <Typography className={classes.headTitle}>{mission.name}</Typography>
                <Typography className={classes.headText}>{mission.description}</Typography>
              </div>
            </>
          }
          detailsClassName={classes.accordionContent}
        >
          {mission.tasks.map((task) => (
            <Box
              onClick={() => setTaskDetails(task)}
              key={task.id}
              className={`${classes.accordionItem} ${task.learned ? classes.learnedAccordionItem : ""}`}
            >
              <div>
                <Typography className={`${classes.contentTitle} ${task.learned ? classes.learnedContentTitle : ""}`}>
                  {task.name}
                </Typography>
                <Typography className={classes.contentText}>{task.data?.content}</Typography>
              </div>
              <Box display="flex" alignItems="center">
                <Typography className={classes.coinBalance}>{task.coins}</Typography>
                <img className={classes.accordionReward} src={oneCoin} alt="oneCoin" />
                <NavigateNextIcon />
              </Box>
            </Box>
          ))}
          <MissionStartButton onClick={() => onStartMission(mission.id)} disabled={!mission.availableToStart}>
            {mission.availableToStart
              ? t("missions.startButton")
              : t("missions.missionStarted", { started: format(new Date(mission.started), "dd.MM.yyyy") })}
          </MissionStartButton>
        </BaseAccordion>
      ))}
      <Dialog open={!!taskDetails} onClose={onClose} scroll="body" fullWidth maxWidth="md">
        <TaskDetails task={taskDetails} onClose={() => onClose()} />
      </Dialog>
    </Box>
  );
};

export default Missions;
